import { ApiError, authStartUrl } from "@/lib/api";
import type { MaxwellBlock } from "@/types/blocks";
import type { MaxwellStreamEvent, RunDriveEvent } from "@/types/maxwell";

/**
 * Stream one Maxwell turn over SSE. POSTs the user's text and invokes
 * `onEvent` for each typed frame the server emits (text deltas, the tool
 * result, the final `done`, or an `error`). Mirrors `lib/api.ts` semantics:
 * `credentials: "include"`, 401 → auth redirect, non-2xx → `ApiError`.
 */
export async function streamMaxwell(
  threadId: string,
  text: string,
  onEvent: (e: MaxwellStreamEvent) => void,
): Promise<void> {
  const res = await fetch(`/api/maxwell/thread/${threadId}/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify({ text }),
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return;
  }
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, safeJson(body) ?? body);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseFrame(frame);
      if (ev) onEvent(ev);
    }
  }
}

/**
 * Drive an in-chat run synchronously over SSE (`POST /api/maxwell/runs/:id/drive`),
 * invoking `onEvent` per step frame until the run pauses, completes, errors, or
 * is promoted to background. `signal` aborts the stream (the SPA aborts on
 * promote so the worker becomes the sole driver). Same fetch semantics as
 * `streamMaxwell`.
 */
export async function driveRun(
  runId: string,
  onEvent: (e: RunDriveEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/maxwell/runs/${runId}/drive`, {
    method: "POST",
    credentials: "include",
    headers: { accept: "text/event-stream" },
    signal,
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return;
  }
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, safeJson(body) ?? body);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseDriveFrame(frame);
      if (ev) onEvent(ev);
    }
  }
}

/** Parse one drive-stream SSE frame into a typed run event. */
function parseDriveFrame(frame: string): RunDriveEvent | null {
  const parsed = rawFrame(frame);
  if (!parsed) return null;
  const { event, p } = parsed;
  switch (event) {
    case "step":
      return {
        kind: "step",
        status: String(p.status ?? ""),
        step_index: Number(p.step_index ?? -1),
        capability: typeof p.capability === "string" ? p.capability : undefined,
        result: p.result,
      };
    case "done":
      return { kind: "done", run_id: String(p.run_id ?? "") };
    case "promoted":
      return { kind: "promoted", run_id: String(p.run_id ?? "") };
    case "paused":
      return { kind: "paused" };
    case "error":
      return { kind: "error", message: String(p.message ?? "") };
    default:
      return null;
  }
}

/** Split one SSE frame into its `event:` name + parsed `data:` JSON object. */
function rawFrame(frame: string): { event: string; p: Record<string, unknown> } | null {
  let event = "";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!event || !data) return null;
  const payload = safeJson(data);
  if (!payload || typeof payload !== "object") return null;
  return { event, p: payload as Record<string, unknown> };
}

/** Parse one SSE frame (`event:` + `data:` lines) into a typed event. */
function parseFrame(frame: string): MaxwellStreamEvent | null {
  let event = "";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!event || !data) return null;
  const payload = safeJson(data);
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  switch (event) {
    case "text":
      return { kind: "text", delta: String(p.delta ?? "") };
    case "tool":
      return { kind: "tool", name: String(p.name ?? ""), result: p.result };
    case "action_preview":
      return {
        kind: "action_preview",
        capability: String(p.capability ?? ""),
        summary: String(p.summary ?? ""),
        details: p.details,
        commit_token: String(p.commit_token ?? ""),
        input: p.input,
      };
    case "blocks":
      return { kind: "blocks", blocks: Array.isArray(p.blocks) ? (p.blocks as MaxwellBlock[]) : [] };
    case "done":
      return { kind: "done", text: String(p.text ?? "") };
    case "error":
      return { kind: "error", message: String(p.message ?? "") };
    default:
      return null;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
